import React from 'react';
import styled from '@emotion/styled';
import { calculateTimeToRead } from 'utils/calculateTimeToRead';
import { Colors } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { ArticleHeroSocialIcons } from './ArticleHeroSocialIcons';
import { Article } from 'data/SingleArticle/types';
import { SmallBlueText } from 'components/typography';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { Link } from 'components/Link/Link';
import { ImageQueryParams } from 'components/Images/RichImageComponent/RichImageComponent';
import {
  ArticleImage,
  defaultImageQueryParams,
} from 'components/Images/ArticleImage/ArticleImage';

type Props = {
  article: Article;
};

const heroImageQueryParams: Readonly<ImageQueryParams> = {
  ...defaultImageQueryParams,
  w: '1520',
  h: '760',
};

export const ArticleHero = ({ article }: Props) => {
  const {
    categoryAssemblyTitle,
    categoryAssemblyHref,
    timeToRead,
    title,
    subtitle,
    author,
    image,
  } = article;
  const trackEvent = useTrackEvent();

  return (
    <section>
      <CategoryWrapper>
        <Link
          href={article.categoryAssemblyHref}
          color="blue"
          onClick={(event) =>
            trackEvent('linkClicked', {
              linkText: categoryAssemblyTitle,
              href: categoryAssemblyHref,
              path: window.location.pathname,
              posYFromTop: event.clientY,
            })
          }
        >
          <SmallBlueText>{categoryAssemblyTitle}</SmallBlueText>
        </Link>
        <TimeToRead>{calculateTimeToRead(timeToRead)}</TimeToRead>
      </CategoryWrapper>
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
      <AuthorWrapper>
        By <span>{author[0].name}</span> &#183;{' '}
        {article.editorialLastPublishedDateComputed}
      </AuthorWrapper>
      <SocialIcons />
      {image.url && (
        <Figure>
          <ArticleImage image={image} queryParams={heroImageQueryParams} />
        </Figure>
      )}
    </section>
  );
};

const AuthorWrapper = styled.div`
  color: ${Colors.grey700};
  margin-top: 20px;
  text-align: center;
`;

const CategoryWrapper = styled.div`
  align-items: center;
  font-size: 14px;
  display: grid;
  justify-content: center;
  grid-template-columns: auto auto;
  grid-column-gap: 10px;
`;

const Figure = styled.figure`
  margin: 20px auto 0 auto;
  ${mediaQueries.desktop} {
    max-width: 768px;
  }
`;

const SocialIcons = styled(ArticleHeroSocialIcons)`
  margin-top: 30px;
  ${mediaQueries.tablet} {
    margin-top: 20px;
  }
`;

const Subtitle = styled.h2`
  color: ${Colors.grey700};
  font-size: 19px;
  font-weight: normal;
  line-height: 27px;
  text-align: center;
  margin: 20px 0 0 0;
  ${mediaQueries.tablet} {
    font-size: 23px;
    line-height: 29px;
  }
`;

const TimeToRead = styled.span`
  color: ${Colors.grey700};
`;

const Title = styled.h1`
  color: ${Colors.black};
  font-weight: 800;
  font-size: 28px;
  line-height: 33px;
  text-align: center;
  margin: 15px auto 0 auto;
  ${mediaQueries.tablet} {
    font-size: 48px;
    line-height: 57px;
    max-width: 768px;
  }
`;
